<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">系統參數</h1>
      </div>
      <div
        v-if="isMemberFieldsTab || ['appearance', 'line', 'email', 'ai_creative', 'member_tier'].includes(activeTab)"
        class="header-actions settings-header-actions"
      >
        <template v-if="isMemberFieldsTab">
          <button
            class="btn btn-primary"
            :disabled="loading || saving || !fieldsLoaded || activeRegistration.loading || activeRegistration.saving || !activeRegistration.loaded"
            @click="handleSave"
          >
            <span v-if="saving || activeRegistration.saving" class="spinner-sm"></span>
            {{ saving || activeRegistration.saving ? '儲存中...' : '儲存設定' }}
          </button>
        </template>

        <template v-else-if="activeTab === 'appearance'">
          <button
            class="btn btn-outline-secondary"
            :disabled="styleLoading || styleSaving"
            @click="handleStyleReset"
          >
            <i class="ri-refresh-line"></i> 恢復預設值
          </button>
          <button
            class="btn btn-primary"
            :disabled="styleLoading || styleSaving"
            @click="handleStyleSave"
          >
            <span v-if="styleSaving" class="spinner-sm"></span>
            {{ styleSaving ? '儲存中...' : '儲存設定' }}
          </button>
        </template>

        <template v-else-if="activeTab === 'line'">
          <button class="btn btn-primary" :disabled="lineLoading || lineSaving" @click="handleLineSave">
            <span v-if="lineSaving" class="spinner-sm"></span>
            {{ lineSaving ? '儲存中...' : '儲存設定' }}
          </button>
        </template>

        <template v-else-if="activeTab === 'email'">
          <button
            class="btn btn-outline-secondary"
            :disabled="notificationEmailSaving"
            @click="handleNotificationEmailSave"
          >
            <span v-if="notificationEmailSaving" class="spinner-sm spinner-sm-dark"></span>
            {{ notificationEmailSaving ? '儲存中...' : '儲存通知 Email' }}
          </button>
          <button
            class="btn btn-outline-secondary"
            :disabled="aiNotifyEmailSaving"
            @click="handleAiNotifyEmailSave"
          >
            <span v-if="aiNotifyEmailSaving" class="spinner-sm spinner-sm-dark"></span>
            {{ aiNotifyEmailSaving ? '儲存中...' : '儲存 AI 通知 Email' }}
          </button>
          <button class="btn btn-primary" :disabled="couponSaving" @click="handleCouponSave">
            <span v-if="couponSaving" class="spinner-sm"></span>
            {{ couponSaving ? '儲存中...' : '儲存到期提醒' }}
          </button>
        </template>

        <template v-else-if="activeTab === 'ai_creative'">
          <button class="btn btn-primary" :disabled="aiCreativeLoading || aiCreativeSaving" @click="handleAiCreativeSave">
            <span v-if="aiCreativeSaving" class="spinner-sm"></span>
            {{ aiCreativeSaving ? '儲存中...' : '儲存設定' }}
          </button>
        </template>

        <template v-else-if="activeTab === 'member_tier'">
          <button class="btn btn-primary" :disabled="memberTierLoading || memberTierSaving" @click="handleMemberTierSave">
            <span v-if="memberTierSaving" class="spinner-sm"></span>
            {{ memberTierSaving ? '儲存中...' : '儲存設定' }}
          </button>
        </template>
      </div>
    </div>

    <div class="page-container">
    <div class="settings-layout">
      <!-- 左側導覽 -->
      <nav class="settings-sidebar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="sidebar-item"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <!-- 右側內容 -->
      <div class="settings-content">

    <!-- 會員欄位設定 -->
    <div v-if="isMemberFieldsTab">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-contacts-line"></i>
            <div>
              <h2 class="settings-card-title">{{ activeTab === 'mall_member_fields' ? '商城會員欄位設定' : '會員欄位設定' }}</h2>
              <p class="settings-card-desc">{{ activeTab === 'mall_member_fields' ? '設定商城註冊與會員資料各欄位的顯示與必填狀態' : '設定 LINE 註冊與會員資料各欄位的顯示與必填狀態' }}</p>
            </div>
          </div>

          <div class="fields-table-wrapper">
            <table class="fields-table">
              <thead>
                <tr>
                  <th class="col-label">欄位名稱</th>
                  <th class="col-toggle">顯示</th>
                  <th class="col-toggle">必填</th>
                  <th class="col-note">備註</th>
                  <th class="col-extra">特殊設定</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="field in memberFields"
                  :key="field.key"
                  :class="{ 'is-locked': field.locked }"
                >
                  <td class="col-label">
                    <span class="field-label">{{ field.label }}</span>
                    <span v-if="field.locked" class="lock-badge">
                      <i class="ri-lock-line"></i> 固定
                    </span>
                  </td>
                  <td class="col-toggle">
                    <label class="toggle-switch" :class="{ disabled: field.locked }">
                      <input
                        type="checkbox"
                        v-model="field.visible"
                        :disabled="field.locked"
                        @change="onVisibleChange(field)"
                      />
                      <span class="slider"></span>
                    </label>
                  </td>
                  <td class="col-toggle">
                    <label class="toggle-switch" :class="{ disabled: field.locked || !field.visible }">
                      <input
                        type="checkbox"
                        v-model="field.required"
                        :disabled="field.locked || !field.visible"
                      />
                      <span class="slider"></span>
                    </label>
                  </td>
                  <td class="col-note">
                    <input
                      type="text"
                      class="note-input"
                      v-model="field.note"
                      placeholder="輸入備註說明..."
                      maxlength="80"
                    />
                    <span v-if="field.locked" class="note-hint">
                      <i class="ri-lock-line"></i> 系統必填欄位
                    </span>
                    <span v-else-if="!field.visible" class="note-hint muted">
                      隱藏時必填自動關閉
                    </span>
                  </td>
                  <td class="col-extra">
                    <label
                      v-if="'allowChangeOnce' in field"
                      class="extra-toggle-row"
                      :title="field.allowChangeOnce ? `${field.label}允許修改一次` : `${field.label}填寫後不可修改`"
                    >
                      <span class="extra-toggle-label">允許修改一次</span>
                      <span class="toggle-switch">
                        <input type="checkbox" v-model="field.allowChangeOnce" />
                        <span class="slider"></span>
                      </span>
                    </label>
                    <label
                      v-if="'allowEdit' in field"
                      class="extra-toggle-row"
                      :title="field.allowEdit ? `${field.label}可自由修改` : `${field.label}填寫後不可修改`"
                    >
                      <span class="extra-toggle-label">允許修改</span>
                      <span class="toggle-switch">
                        <input type="checkbox" v-model="field.allowEdit" />
                        <span class="slider"></span>
                      </span>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="settings-card-footer">
            <div v-if="saveError" class="alert alert-danger">{{ saveError }}</div>
            <div v-if="saveSuccess" class="alert alert-success">設定已儲存</div>
          </div>
        </div>

        <div v-if="activeRegistration.loading" class="loading-state settings-card-spaced">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else class="settings-card settings-card-spaced">
          <div class="settings-card-header">
            <i class="ri-shield-check-line"></i>
            <div>
              <h2 class="settings-card-title">{{ registrationCopy.title }}</h2>
              <p class="settings-card-desc">{{ registrationCopy.desc }}</p>
            </div>
          </div>

          <div class="reg-settings-body">
            <div class="reg-setting-row">
              <div class="reg-setting-label">
                <span class="reg-setting-name">
                  <i class="ri-message-2-line"></i>
                  手機簡訊驗證
                </span>
                <span class="reg-setting-desc">{{ registrationCopy.sms }}</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="activeRegistration.settings.requireSmsVerification" />
                <span class="slider"></span>
              </label>
            </div>

            <div class="reg-setting-row">
              <div class="reg-setting-label">
                <span class="reg-setting-name">
                  <i class="ri-shield-line"></i>
                  隱私權條款同意
                </span>
                <span class="reg-setting-desc">{{ registrationCopy.privacy }}</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="activeRegistration.settings.requirePrivacyConsent" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="activeRegistration.saveError" class="alert alert-danger">{{ activeRegistration.saveError }}</div>
            <div v-if="activeRegistration.saveSuccess" class="alert alert-success">設定已儲存</div>
          </div>
        </div>
      </template>
    </div>

    <!-- LIFF URLs -->
    <div v-if="activeTab === 'liff_urls'">
      <div v-if="liffLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <template v-else>
        <div v-if="liffEntries.length === 0" class="empty-state">
          <i class="ri-links-line"></i>
          <p>尚未設定 LINE 官方帳號，請先至 LINE 設定頁面建立</p>
        </div>

        <template v-else>
          <div
            v-for="config in liffEntries"
            :key="config.configId"
            class="settings-card liff-card"
          >
            <div class="settings-card-header">
              <i class="ri-line-line"></i>
              <div>
                <h2 class="settings-card-title">{{ config.configName }}</h2>
                <p class="settings-card-desc">
                  頻道 ID：{{ config.channelId }}
                  <span class="badge-shared">共用 LIFF</span>
                  <span class="badge-member-mode" :class="liffPathPrefix === '/wixtar' ? 'is-wixtar' : 'is-lockline'">
                    {{ memberSystemLabel }}
                  </span>
                  <span v-if="!config.isActive" class="badge-inactive">停用</span>
                </p>
              </div>
            </div>

            <div v-if="!config.commonLiffId" class="liff-empty">
              <i class="ri-information-line"></i> 此設定尚未設定共用入口 LIFF ID
            </div>

            <div v-else class="liff-list">
              <div class="liff-row liff-row-endpoint">
                <div class="liff-route-meta">
                  <span class="liff-route-icon"><i class="ri-window-line"></i></span>
                  <div class="liff-route-label-stack">
                    <span class="liff-route-label">LIFF Endpoint URL</span>
                    <span class="liff-route-hint">LINE Developers → LIFF 應用程式 → Endpoint URL（與會員模式前綴一致）</span>
                  </div>
                </div>
                <div class="liff-info">
                  <a
                    :href="resolveLiffEndpointUrl(config)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="liff-url"
                  >
                    {{ resolveLiffEndpointUrl(config) }}
                    <i class="ri-external-link-line"></i>
                  </a>
                </div>
                <button
                  type="button"
                  class="btn-copy"
                  :class="{ copied: copiedId === copyKeyEndpoint(config) }"
                  @click.stop="copyUrl(resolveLiffEndpointUrl(config), copyKeyEndpoint(config))"
                  :title="copiedId === copyKeyEndpoint(config) ? '已複製' : '複製網址'"
                >
                  <i :class="copiedId === copyKeyEndpoint(config) ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                  {{ copiedId === copyKeyEndpoint(config) ? '已複製' : '複製' }}
                </button>
              </div>
              <div class="liff-row liff-row-callback">
                <div class="liff-route-meta">
                  <span class="liff-route-icon"><i class="ri-shield-keyhole-line"></i></span>
                  <div class="liff-route-label-stack">
                    <span class="liff-route-label">LINE Login Callback URL</span>
                    <span class="liff-route-hint">LINE Developers → LINE Login → Callback URL</span>
                  </div>
                </div>
                <div class="liff-info">
                  <a
                    :href="resolveLineLoginCallbackUrl(config)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="liff-url"
                  >
                    {{ resolveLineLoginCallbackUrl(config) }}
                    <i class="ri-external-link-line"></i>
                  </a>
                </div>
                <button
                  type="button"
                  class="btn-copy"
                  :class="{ copied: copiedId === copyKeyCallback(config) }"
                  @click.stop="copyUrl(resolveLineLoginCallbackUrl(config), copyKeyCallback(config))"
                  :title="copiedId === copyKeyCallback(config) ? '已複製' : '複製網址'"
                >
                  <i :class="copiedId === copyKeyCallback(config) ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                  {{ copiedId === copyKeyCallback(config) ? '已複製' : '複製' }}
                </button>
              </div>
              <div
                v-for="route in routesFor(config)"
                :key="route.key"
                class="liff-row"
              >
                <div class="liff-route-meta">
                  <span class="liff-route-icon"><i :class="route.icon"></i></span>
                  <div class="liff-route-label-stack">
                    <span class="liff-route-label">{{ route.label }}</span>
                    <span v-if="route.hint" class="liff-route-hint">{{ route.hint }}</span>
                  </div>
                </div>
                <div class="liff-info">
                  <a
                    v-if="route.suffix"
                    :href="buildLiffOpenUrl(config, route.suffix)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="liff-url"
                  >
                    {{ buildLiffOpenUrl(config, route.suffix) }}
                    <i class="ri-external-link-line"></i>
                  </a>
                  <span v-else class="liff-url-missing">尚無網址</span>
                </div>
                <button
                  v-if="route.suffix"
                  type="button"
                  class="btn-copy"
                  :class="{ copied: copiedId === copyKeyLiffRoute(config, route.suffix) }"
                  @click.stop="copyUrl(buildLiffOpenUrl(config, route.suffix), copyKeyLiffRoute(config, route.suffix))"
                  :title="copiedId === copyKeyLiffRoute(config, route.suffix) ? '已複製' : '複製網址'"
                >
                  <i :class="copiedId === copyKeyLiffRoute(config, route.suffix) ? 'ri-check-line' : 'ri-file-copy-line'"></i>
                  {{ copiedId === copyKeyLiffRoute(config, route.suffix) ? '已複製' : '複製' }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- 外觀設定 -->
    <div v-if="activeTab === 'appearance'">
      <div v-if="styleLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>
      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-palette-line"></i>
            <div>
              <h2 class="settings-card-title">會員中心外觀設定</h2>
              <p class="settings-card-desc">自訂品牌色彩、導覽列與各頁面視覺風格</p>
            </div>
          </div>

          <div class="appearance-body">
            <!-- 整體品牌色 -->
            <div class="color-group">
              <h3 class="color-group-title">整體品牌</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">畫面背景色</span>
                    <span class="color-row-desc">會員中心整體畫面的背景顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.pageBg" class="color-input" />
                    <input type="text" v-model="styleSettings.pageBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.pageBg = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">Page Title 背景色</span>
                    <span class="color-row-desc">各頁面頂部標題列的背景顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.brandColor" class="color-input" />
                    <input type="text" v-model="styleSettings.brandColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.brandColor = '#1a1a1a'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">Page Title 文字色</span>
                    <span class="color-row-desc">標題列文字與圖示的顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.brandTextColor" class="color-input" />
                    <input type="text" v-model="styleSettings.brandTextColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.brandTextColor = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 強調色 / 互動元素 -->
            <div class="color-group">
              <h3 class="color-group-title">強調色（Tab 選中、導覽 Active）</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">強調色</span>
                    <span class="color-row-desc">Tab 選中背景色、底部導覽 Active 圖示顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.accentColor" class="color-input" />
                    <input type="text" v-model="styleSettings.accentColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.accentColor = '#f5a623'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">強調色文字</span>
                    <span class="color-row-desc">強調色背景上的文字顏色（預設白色）</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.accentTextColor" class="color-input" />
                    <input type="text" v-model="styleSettings.accentTextColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.accentTextColor = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 底部導覽列 -->
            <div class="color-group">
              <h3 class="color-group-title">底部導覽列</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">導覽列背景色</span>
                    <span class="color-row-desc">底部導覽 Bar 的背景顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.navBg" class="color-input" />
                    <input type="text" v-model="styleSettings.navBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.navBg = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">未選中圖示色</span>
                    <span class="color-row-desc">底部導覽未選中的圖示與文字顏色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.navInactiveColor" class="color-input" />
                    <input type="text" v-model="styleSettings.navInactiveColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.navInactiveColor = '#b0b0b0'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 會員資訊區 -->
            <div class="color-group">
              <h3 class="color-group-title">會員資訊區</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">頭像背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.avatarBg" class="color-input" />
                    <input type="text" v-model="styleSettings.avatarBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.avatarBg = '#e8e8e8'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">頭像圖示色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.avatarIconColor" class="color-input" />
                    <input type="text" v-model="styleSettings.avatarIconColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.avatarIconColor = '#bdbdbd'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">會員姓名色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.profileNameColor" class="color-input" />
                    <input type="text" v-model="styleSettings.profileNameColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.profileNameColor = '#1a1a1a'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">會員編號色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.profileCodeColor" class="color-input" />
                    <input type="text" v-model="styleSettings.profileCodeColor" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.profileCodeColor = '#888888'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 首頁專屬設定 分隔標題 -->
            <div class="home-section-divider">
              <span class="home-section-label">以下為首頁專屬設定</span>
            </div>

            <!-- 紅利點數 -->
            <div class="color-group">
              <h3 class="color-group-title">
                <span class="group-badge">首頁</span>
                <span class="stat-preview" :style="{ background: styleSettings.pointsBg, color: styleSettings.pointsText, opacity: styleSettings.showPoints ? 1 : 0.35 }">{{ styleSettings.pointsLabel || '紅利點數' }}</span>
                <label class="show-toggle">
                  <input type="checkbox" v-model="styleSettings.showPoints" />
                  <span class="slider"></span>
                  <span class="show-toggle-label">{{ styleSettings.showPoints ? '顯示' : '隱藏' }}</span>
                </label>
              </h3>
              <div v-if="styleSettings.showPoints" class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">顯示文字</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="text" v-model="styleSettings.pointsLabel" class="label-text-input" maxlength="10" placeholder="紅利點數" />
                    <button class="reset-btn" @click="styleSettings.pointsLabel = '紅利點數'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.pointsBg" class="color-input" />
                    <input type="text" v-model="styleSettings.pointsBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.pointsBg = '#1a1a1a'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">文字色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.pointsText" class="color-input" />
                    <input type="text" v-model="styleSettings.pointsText" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.pointsText = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 優惠票券 -->
            <div class="color-group">
              <h3 class="color-group-title">
                <span class="group-badge">首頁</span>
                <span class="stat-preview" :style="{ background: styleSettings.couponsBg, color: styleSettings.couponsText, opacity: styleSettings.showCoupons ? 1 : 0.35 }">{{ styleSettings.couponsLabel || '優惠票券' }}</span>
                <label class="show-toggle">
                  <input type="checkbox" v-model="styleSettings.showCoupons" />
                  <span class="slider"></span>
                  <span class="show-toggle-label">{{ styleSettings.showCoupons ? '顯示' : '隱藏' }}</span>
                </label>
              </h3>
              <div v-if="styleSettings.showCoupons" class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">顯示文字</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="text" v-model="styleSettings.couponsLabel" class="label-text-input" maxlength="10" placeholder="優惠票券" />
                    <button class="reset-btn" @click="styleSettings.couponsLabel = '優惠票券'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.couponsBg" class="color-input" />
                    <input type="text" v-model="styleSettings.couponsBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.couponsBg = '#f5a623'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">文字色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.couponsText" class="color-input" />
                    <input type="text" v-model="styleSettings.couponsText" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.couponsText = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 集章數 -->
            <div class="color-group">
              <h3 class="color-group-title">
                <span class="group-badge">首頁</span>
                <span class="stat-preview" :style="{ background: styleSettings.stampsBg, color: styleSettings.stampsText, border: '1px solid #e0e0e0', opacity: styleSettings.showStamps ? 1 : 0.35 }">{{ styleSettings.stampsLabel || '集章數' }}</span>
                <label class="show-toggle">
                  <input type="checkbox" v-model="styleSettings.showStamps" />
                  <span class="slider"></span>
                  <span class="show-toggle-label">{{ styleSettings.showStamps ? '顯示' : '隱藏' }}</span>
                </label>
              </h3>
              <div v-if="styleSettings.showStamps" class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">顯示文字</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="text" v-model="styleSettings.stampsLabel" class="label-text-input" maxlength="10" placeholder="集章數" />
                    <button class="reset-btn" @click="styleSettings.stampsLabel = '集章數'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.stampsBg" class="color-input" />
                    <input type="text" v-model="styleSettings.stampsBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.stampsBg = '#ffffff'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">文字色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.stampsText" class="color-input" />
                    <input type="text" v-model="styleSettings.stampsText" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.stampsText = '#222222'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 導航項目配置 -->
            <div class="color-group">
              <h3 class="color-group-title"><span class="group-badge">首頁</span>導航項目</h3>
              <p class="color-group-desc">拖拉可調整排列順序，並設定每個項目要顯示在「功能按鈕」或「選單」，或隱藏不顯示。</p>
              <div class="nav-items-config">
                <div
                  v-for="(item, idx) in styleSettings.navItems"
                  :key="item.key"
                  class="nav-item-row"
                  draggable="true"
                  :class="{ 'drag-over': dragOverIdx === idx }"
                  @dragstart="onDragStart(idx)"
                  @dragover.prevent="onDragOver(idx)"
                  @drop.prevent="onDrop(idx)"
                  @dragend="onDragEnd"
                >
                  <span class="nav-drag-handle" title="拖拉排序">
                    <i class="ri-drag-move-2-line"></i>
                  </span>
                  <span class="nav-item-label">
                    <i :class="NAV_ITEM_ICONS[item.key]"></i>
                    <input
                      type="text"
                      v-model="styleSettings.navItems[idx].label"
                      class="nav-label-input"
                      maxlength="12"
                      @mousedown.stop
                    />
                  </span>
                  <div class="placement-selector">
                    <button
                      v-for="opt in PLACEMENT_OPTS"
                      :key="opt.value"
                      :class="['placement-btn', { active: item.placement === opt.value }]"
                      @click="styleSettings.navItems[idx] = { ...item, placement: opt.value }"
                    >{{ opt.label }}</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 功能按鈕顏色 -->
            <div class="color-group">
              <h3 class="color-group-title"><span class="group-badge">首頁</span>功能按鈕顏色</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">圖示框背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.iconsBg" class="color-input" />
                    <input type="text" v-model="styleSettings.iconsBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.iconsBg = '#f5f5f5'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">圖示與文字色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.iconsText" class="color-input" />
                    <input type="text" v-model="styleSettings.iconsText" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.iconsText = '#333333'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 選單項目顏色 -->
            <div class="color-group">
              <h3 class="color-group-title"><span class="group-badge">首頁</span>選單項目顏色</h3>
              <div class="color-rows">
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">圖示圓圈背景色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.menuIconBg" class="color-input" />
                    <input type="text" v-model="styleSettings.menuIconBg" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.menuIconBg = '#f5f5f5'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
                <div class="color-row">
                  <div class="color-row-label">
                    <span class="color-row-name">圖示與文字色</span>
                  </div>
                  <div class="color-row-ctrl">
                    <input type="color" v-model="styleSettings.menuText" class="color-input" />
                    <input type="text" v-model="styleSettings.menuText" class="hex-input" maxlength="7" />
                    <button class="reset-btn" @click="styleSettings.menuText = '#333333'" title="重設為預設值">
                      <i class="ri-refresh-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="styleSaveError" class="alert alert-danger">{{ styleSaveError }}</div>
            <div v-if="styleSaveSuccess" class="alert alert-success">設定已儲存</div>
          </div>
        </div>
      </template>
    </div>

    <!-- LINE 設定 -->
    <div v-if="activeTab === 'line'">
      <div v-if="lineLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>
      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-menu-2-line" style="color: #06c755;"></i>
            <div>
              <h2 class="settings-card-title">LINE 圖文選單模式</h2>
              <p class="settings-card-desc">設定 LINE 圖文選單的預設運作模式，影響整個帳號的選單呈現邏輯</p>
            </div>
          </div>

          <div class="rich-menu-mode-body">
            <div
              class="mode-option"
              :class="{ active: lineSettings.richMenuMode === 'schedule' }"
              @click="lineSettings.richMenuMode = 'schedule'"
            >
              <div class="mode-radio">
                <span class="radio-dot" :class="{ checked: lineSettings.richMenuMode === 'schedule' }"></span>
              </div>
              <div class="mode-icon schedule">
                <i class="ri-calendar-schedule-line"></i>
              </div>
              <div class="mode-info">
                <span class="mode-title">排程模式（一般模式）</span>
                <span class="mode-desc">依排程日期顯示對應的圖文選單，不同時段可設定不同選單，適合活動型、時段促銷場景。</span>
              </div>
            </div>

            <div
              class="mode-option"
              :class="{ active: lineSettings.richMenuMode === 'member' }"
              @click="lineSettings.richMenuMode = 'member'"
            >
              <div class="mode-radio">
                <span class="radio-dot" :class="{ checked: lineSettings.richMenuMode === 'member' }"></span>
              </div>
              <div class="mode-icon member">
                <i class="ri-vip-crown-line"></i>
              </div>
              <div class="mode-info">
                <span class="mode-title">會員模式</span>
                <span class="mode-desc">依會員等級顯示對應的圖文選單，不同等級的會員可看到不同的選單內容，適合會員分級場景。</span>
              </div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="lineSaveError" class="alert alert-danger">{{ lineSaveError }}</div>
            <div v-if="lineSaveSuccess" class="alert alert-success">設定已儲存</div>
          </div>
        </div>

        <!-- 跨 OA 會員共用模式（唯讀，由平台管理員控制） -->
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-links-line" style="color: #06c755;"></i>
            <div>
              <h2 class="settings-card-title">跨 OA 會員共用模式</h2>
              <p class="settings-card-desc">當會員加入同品牌其他 LINE 官方帳號好友時的綁定方式（由平台管理員設定）</p>
            </div>
          </div>
          <div class="cross-oa-mode-display">
            <div class="cross-oa-current-row">
              <div>
                <p class="cross-oa-label">目前模式</p>
                <div class="mode-badge" :class="lineSettings.crossOaBindingMode === 'auto' ? 'mode-auto' : 'mode-phone'">
                  <i :class="lineSettings.crossOaBindingMode === 'auto' ? 'ri-link-m' : 'ri-smartphone-line'"></i>
                  <span v-if="lineSettings.crossOaBindingMode === 'auto'">靜默共用</span>
                  <span v-else>電話驗證共用</span>
                </div>
                <p class="mode-description">
                  <span v-if="lineSettings.crossOaBindingMode === 'auto'">
                    會員加入同品牌其他 LINE OA 好友時，系統自動繼承既有會員綁定，無需任何額外操作。
                  </span>
                  <span v-else>
                    會員加入同品牌其他 LINE OA 好友後，需自行開啟 LIFF 並輸入手機號碼完成綁定。
                  </span>
                </p>
              </div>
            </div>

            <!-- 其他可用模式說明 -->
            <div class="cross-oa-other-modes">
              <p class="other-modes-title">
                <i class="ri-information-line"></i>
                其他可用模式
              </p>
              <div class="other-mode-item" v-if="lineSettings.crossOaBindingMode !== 'auto'">
                <div class="mode-badge mode-auto">
                  <i class="ri-link-m"></i>
                  靜默共用
                </div>
                <p class="other-mode-desc">會員加入好友當下自動繼承既有會員綁定，無需任何操作，體驗最順暢。</p>
              </div>
              <div class="other-mode-item" v-if="lineSettings.crossOaBindingMode !== 'phone_verification'">
                <div class="mode-badge mode-phone">
                  <i class="ri-smartphone-line"></i>
                  電話驗證共用
                </div>
                <p class="other-mode-desc">會員加入好友後需自行開啟 LIFF 輸入手機號碼完成綁定。</p>
              </div>
            </div>

            <div class="cross-oa-contact-hint">
              <i class="ri-customer-service-2-line"></i>
              <span>如需變更模式，請聯繫您的產品顧問。</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Email 通知設定 -->
    <div v-if="activeTab === 'email'">
        <!-- 通知收件 Email -->
        <div class="settings-card" style="margin-bottom: 20px;">
          <div class="settings-card-header">
            <i class="ri-mail-download-line" style="color: #e67e22;"></i>
            <div>
              <h2 class="settings-card-title">通知收件 Email</h2>
              <p class="settings-card-desc">系統通知（訂閱到期提醒、扣款失敗通知等）的收件地址。留空則寄至帳號登入 Email。</p>
            </div>
          </div>

          <div class="settings-card-body">
            <div class="form-group">
              <label class="form-label">通知收件地址</label>
              <input
                v-model="notificationEmail"
                type="text"
                class="form-control"
                placeholder="例：billing@example.com, finance@example.com"
              />
              <div class="form-hint">
                最多可設定 5 個收件地址，以半形逗號（,）隔開。<br>
                訂閱到期提醒（到期前 30、7、0 天）、扣款失敗通知等系統信件將同時寄至所有地址。<br>
                若留空，通知將寄至帳號管理員的登入 Email。
              </div>
              <div v-if="notificationEmailPreview.length > 0" class="email-tag-list">
                <span v-for="(em, i) in notificationEmailPreview" :key="i" class="email-tag">{{ em }}</span>
              </div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="notificationEmailError" class="alert alert-danger">{{ notificationEmailError }}</div>
            <div v-if="notificationEmailSuccess" class="alert alert-success">通知 Email 已更新</div>
          </div>
        </div>

        <!-- AI 客服通知 Email -->
        <div class="settings-card" style="margin-bottom: 20px;">
          <div class="settings-card-header">
            <i class="ri-robot-line" style="color: #5b6bc0;"></i>
            <div>
              <h2 class="settings-card-title">AI 客服通知 Email</h2>
              <p class="settings-card-desc">當 AI 客服無法回答（嚴格模式超出知識庫），或偵測到客人要求聯繫真人客服時，寄送通知給指定收件人。</p>
            </div>
          </div>

          <div class="settings-card-body">
            <div class="form-group">
              <label class="form-label">AI 通知收件地址</label>
              <input
                v-model="aiNotifyEmail"
                type="text"
                class="form-control"
                placeholder="例：cs@example.com, manager@example.com"
              />
              <div class="form-hint">
                最多可設定 5 個收件地址，以半形逗號（,）隔開。<br>
                觸發情況：(1) 嚴格模式＋知識庫檢索開啟，且問題超出訓練範圍；(2) 客人表達要聯繫真人／人工客服（關鍵字或 AI 判斷）。<br>
                同一對話 5 分鐘內最多通知一次；若留空則不發送通知。
              </div>
              <div v-if="aiNotifyEmailPreview.length > 0" class="email-tag-list">
                <span v-for="(em, i) in aiNotifyEmailPreview" :key="i" class="email-tag">{{ em }}</span>
              </div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="aiNotifyEmailError" class="alert alert-danger">{{ aiNotifyEmailError }}</div>
            <div v-if="aiNotifyEmailSuccess" class="alert alert-success">AI 通知 Email 已更新</div>
          </div>
        </div>

        <!-- 優惠券到期前提醒 -->
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-coupon-line" style="color: #27ae60;"></i>
            <div>
              <h2 class="settings-card-title">優惠券到期前提醒</h2>
              <p class="settings-card-desc">自動在優惠券到期前，透過 LINE 或 Email 提醒會員使用。</p>
            </div>
          </div>

          <div class="settings-card-body">
            <div class="form-group">
              <div style="display: flex; align-items: center; gap: 12px;">
                <label class="toggle-switch">
                  <input type="checkbox" v-model="couponSettings.expiryReminderEnabled" />
                  <span class="slider"></span>
                </label>
                <span style="font-size: 14px; font-weight: 500; color: #2d3748;">啟用優惠券到期前提醒</span>
              </div>
              <div class="form-hint" style="margin-top: 8px;">啟用後，系統每天 09:00 自動發送提醒給有即將到期優惠券的會員（優先 LINE push，次為 Email）。</div>
            </div>

            <div v-if="couponSettings.expiryReminderEnabled" class="form-group">
              <label class="form-label">提前幾天提醒</label>
              <select v-model="couponSettings.expiryReminderDays" class="form-control" style="max-width: 180px;">
                <option :value="1">到期前 1 天</option>
                <option :value="3">到期前 3 天</option>
                <option :value="7">到期前 7 天</option>
                <option :value="14">到期前 14 天</option>
              </select>
              <div class="form-hint">會員有優惠券將在此天數後到期時，系統即發送提醒。</div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="couponSaveError" class="alert alert-danger">{{ couponSaveError }}</div>
            <div v-if="couponSaveSuccess" class="alert alert-success">設定已儲存</div>
          </div>
        </div>

    </div>

    <!-- AI 創意設定 -->
    <div v-if="activeTab === 'ai_creative'">
      <div v-if="aiCreativeLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>
      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-magic-line" style="color: #7c3aed;"></i>
            <div>
              <h2 class="settings-card-title">AI 創意設定</h2>
              <p class="settings-card-desc">設定圖文選單 AI 生圖使用的企業色與品牌名稱</p>
            </div>
          </div>

          <div class="settings-card-body">
            <div v-if="aiCreativeQuota" class="ai-quota-summary">
              <div class="ai-quota-item">
                <span class="ai-quota-label">可用餘額</span>
                <strong class="ai-quota-value">NT$ {{ Number(aiCreativeQuota.availableTwd || 0).toLocaleString() }}</strong>
              </div>
              <div class="ai-quota-item">
                <span class="ai-quota-label">本月免費額度剩餘</span>
                <span>NT$ {{ Number(aiCreativeQuota.freeTwdRemaining || 0).toLocaleString() }} / {{ Number(aiCreativeQuota.monthlyFreeTwd || 0).toLocaleString() }}</span>
              </div>
              <div class="ai-quota-item">
                <span class="ai-quota-label">儲值餘額</span>
                <span>NT$ {{ Number(aiCreativeQuota.purchasedTwd || 0).toLocaleString() }}</span>
              </div>
              <p class="form-hint">每次生成依實際 token 用量扣款，餘額不足可直接線上儲值。</p>
            </div>

            <div class="form-group">
              <label class="form-label">線上儲值</label>
              <div v-if="aiTopupPackages.length" class="ai-topup-packages">
                <button
                  v-for="pkg in aiTopupPackages"
                  :key="pkg.packageCode"
                  type="button"
                  class="ai-topup-package-btn"
                  :disabled="aiTopupProcessing"
                  @click="handleAiCreativeTopup(pkg.packageCode)"
                >
                  <span class="pkg-meta">儲值 NT$ {{ Number(pkg.amountTwd || 0).toLocaleString() }}</span>
                </button>
              </div>
              <div v-else class="form-hint">暫無可用儲值方案</div>
              <div class="form-hint">儲值成功後系統會即時入帳，不與 ChatMate 儲值額度共用。</div>
              <button
                v-if="isDevMode && aiTopupOrders.length"
                type="button"
                class="btn btn-outline-secondary"
                style="margin-top: 8px;"
                :disabled="aiTopupProcessing"
                @click="handleAiCreativeDevComplete(aiTopupOrders[0]?.id)"
              >
                DEV：模擬最新訂單付款成功
              </button>
            </div>

            <div class="form-group">
              <label class="form-label">儲值紀錄</label>
              <div v-if="aiTopupOrders.length" class="ai-topup-orders">
                <table>
                  <thead>
                    <tr>
                      <th>建立時間</th>
                      <th>金額</th>
                      <th>狀態</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in aiTopupOrders" :key="row.id">
                      <td>{{ formatDateTime(row.created_at) }}</td>
                      <td>NT$ {{ Number(row.amount_twd || 0).toLocaleString() }}</td>
                      <td>
                        <span class="ai-topup-status" :class="`status-${row.status}`">
                          {{ formatTopupStatus(row.status) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="form-hint">尚無儲值紀錄</div>
            </div>

            <div class="form-group">
              <label class="form-label">品牌名稱（選填）</label>
              <input
                v-model="aiCreativeSettings.brandName"
                type="text"
                class="form-control"
                placeholder="供 AI 理解品牌語境，不會直接渲染在圖片上"
                maxlength="60"
              />
            </div>

            <div class="form-group">
              <label class="toggle-row">
                <label class="toggle-switch">
                  <input type="checkbox" v-model="aiCreativeSettings.syncFromMemberHomeStyle" />
                  <span class="slider"></span>
                </label>
                <span>同步會員中心外觀設定的品牌色</span>
              </label>
              <div class="form-hint">開啟時，若下方未指定色彩，將自動採用「外觀設定」的主色與強調色。</div>
            </div>

            <div class="color-grid">
              <div class="form-group">
                <label class="form-label">主色（企業色）</label>
                <div class="color-field-row">
                  <input
                    :value="normalizeColorForInput(aiCreativeSettings.brandColor, 'brandColor')"
                    type="color"
                    class="color-input"
                    @input="handleAiCreativeColorInput('brandColor', $event.target.value)"
                  />
                  <input
                    v-model="aiCreativeSettings.brandColor"
                    type="text"
                    class="form-control hex-input"
                    placeholder="留空則依同步設定"
                    maxlength="7"
                  />
                  <button type="button" class="reset-btn" @click="aiCreativeSettings.brandColor = ''">清除</button>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">輔色</label>
                <div class="color-field-row">
                  <input
                    :value="normalizeColorForInput(aiCreativeSettings.brandSecondaryColor, 'brandSecondaryColor')"
                    type="color"
                    class="color-input"
                    @input="handleAiCreativeColorInput('brandSecondaryColor', $event.target.value)"
                  />
                  <input
                    v-model="aiCreativeSettings.brandSecondaryColor"
                    type="text"
                    class="form-control hex-input"
                    placeholder="留空則依同步設定"
                    maxlength="7"
                  />
                  <button type="button" class="reset-btn" @click="aiCreativeSettings.brandSecondaryColor = ''">清除</button>
                </div>
              </div>
            </div>
          </div>

          <div class="settings-card-footer">
            <div v-if="aiCreativeSaveError" class="alert alert-danger">{{ aiCreativeSaveError }}</div>
            <div v-if="aiCreativeSaveSuccess" class="alert alert-success">AI 創意設定已儲存</div>
          </div>
        </div>
      </template>
    </div>

    <form ref="aiTopupFormRef" :action="aiTopupPaymentUrl" method="POST" target="_self" style="display: none;">
      <input v-for="(value, key) in aiTopupOrderData" :key="key" type="hidden" :name="key" :value="value" />
    </form>

    <!-- 會員等級設定 -->
    <div v-if="activeTab === 'member_tier'">
      <div v-if="memberTierLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>
      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-vip-crown-line"></i>
            <div>
              <h2 class="settings-card-title">會員等級計算模式</h2>
              <p class="settings-card-desc">設定會員等級的累積週期與自動降等規則</p>
            </div>
          </div>

          <div class="reg-settings-body">
            <div class="reg-setting-row">
              <div class="reg-setting-label">
                <span class="reg-setting-name">
                  <i class="ri-refresh-line"></i>
                  計算模式
                </span>
                <span class="reg-setting-desc">
                  決定會員等級的累積週期。<strong>終身累積</strong>：消費金額/次數永久累積不重置。<strong>年度固定月份</strong>：每年指定月份重新計算，不符合條件可降等。<strong>升等後一年</strong>：從升等日起算一年內維持，期滿重新審核。
                </span>
              </div>
              <select v-model="memberTierSettings.calculationMode" class="form-control" style="max-width: 200px;">
                <option value="lifetime">終身累積</option>
                <option value="annual_calendar">年度固定月份</option>
                <option value="annual_rolling">升等後一年</option>
              </select>
            </div>

            <template v-if="memberTierSettings.calculationMode !== 'lifetime'">
              <div class="reg-setting-row">
                <div class="reg-setting-label">
                  <span class="reg-setting-name">
                    <i class="ri-arrow-down-circle-line"></i>
                    自動降等
                  </span>
                  <span class="reg-setting-desc">
                    啟用後，達到計算週期末時若未符合等級條件，系統將自動將會員降至符合資格的等級
                  </span>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="memberTierSettings.annualDowngradeEnabled" />
                  <span class="slider"></span>
                </label>
              </div>

              <div class="reg-setting-row" v-if="memberTierSettings.calculationMode === 'annual_calendar' && memberTierSettings.annualDowngradeEnabled">
                <div class="reg-setting-label">
                  <span class="reg-setting-name">
                    <i class="ri-calendar-line"></i>
                    降等審核月份
                  </span>
                  <span class="reg-setting-desc">
                    每年幾月執行降等審核，將不符合條件的會員調整至適當等級
                  </span>
                </div>
                <select v-model="memberTierSettings.annualDowngradeMonth" class="form-control" style="max-width: 120px;">
                  <option v-for="m in 12" :key="m" :value="m">{{ m }} 月</option>
                </select>
              </div>
            </template>

          </div>

          <div class="settings-card-footer">
            <div v-if="memberTierSaveError" class="alert alert-danger">{{ memberTierSaveError }}</div>
            <div v-if="memberTierSaveSuccess" class="alert alert-success">會員等級設定已儲存</div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="activeTab === 'sms'" class="settings-content-panel">
      <div class="settings-card">
        <div class="settings-card-header">
          <h2 class="settings-card-title">簡訊點數</h2>
          <p class="settings-card-desc">餘額不足時簡訊不會送出。低餘額提醒在餘額跌破門檻時寄一封。</p>
        </div>
        <div class="settings-card-body">
          <p v-if="smsSummary">目前餘額：<strong>{{ formatSmsPoints(smsSummary.balance) }}</strong> 點</p>
          <div class="form-group">
            <label>低餘額門檻</label>
            <input v-model.number="smsThreshold" type="number" min="0" step="1" class="form-control" />
          </div>
          <button type="button" class="btn btn-primary" :disabled="smsSaving" @click="saveSmsThreshold">
            {{ smsSaving ? '儲存中...' : '儲存門檻' }}
          </button>
          <p v-if="smsMessage" class="form-hint">{{ smsMessage }}</p>
          <div v-if="smsLoading">載入中...</div>
          <div v-else class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>類型</th>
                  <th>點數</th>
                  <th>餘額</th>
                  <th>金額</th>
                  <th>來源</th>
                  <th>手機</th>
                  <th>內容</th>
                  <th>備註</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!smsEntries.length">
                  <td colspan="9">尚無紀錄</td>
                </tr>
                <tr v-for="row in smsEntries" :key="row.id">
                  <td>{{ formatSmsTime(row.createdAt) }}</td>
                  <td>{{ smsEntryLabel(row.entryType) }}</td>
                  <td>{{ formatSmsPoints(row.pointsDelta) }}</td>
                  <td>{{ formatSmsPoints(row.balanceAfter) }}</td>
                  <td>{{ row.amountTwd == null ? '' : row.amountTwd }}</td>
                  <td>{{ smsSourceLabel(row.source) }}</td>
                  <td>{{ row.phoneMasked }}</td>
                  <td>{{ row.messageBody || row.errorMessage }}</td>
                  <td>{{ row.note }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="smsPagination.total > smsPagination.pageSize">
            <button type="button" class="btn btn-secondary" :disabled="smsPagination.page <= 1" @click="loadSmsPoints(smsPagination.page - 1)">上一頁</button>
            <span>{{ smsPagination.page }} / {{ smsPageCount }}</span>
            <button type="button" class="btn btn-secondary" :disabled="smsPagination.page >= smsPageCount" @click="loadSmsPoints(smsPagination.page + 1)">下一頁</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Wixtar 整合 -->
    <div v-if="activeTab === 'wixtar'">
      <WixtarPanel />
    </div>

    <!-- 未來頁籤的佔位 -->
    <div v-if="activeTab !== 'member_fields' && activeTab !== 'mall_member_fields' && activeTab !== 'liff_urls' && activeTab !== 'appearance' && activeTab !== 'line' && activeTab !== 'email' && activeTab !== 'ai_creative' && activeTab !== 'member_tier' && activeTab !== 'wixtar' && activeTab !== 'sms'" class="coming-soon">
      <i class="ri-tools-line"></i>
      <p>此功能尚未開放，敬請期待</p>
    </div>

      </div><!-- end settings-content -->
    </div><!-- end settings-layout -->
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings'
import { smsEntryLabel, smsSourceLabel, formatSmsPoints } from '@/utils/smsPointLabels'
import { aiCreativeService } from '@/api/modules/aiCreative.js'
import lineConfigApi from '@/api/modules/lineConfig'
import { shareRewardCampaignService } from '@/api/modules/shareRewardCampaigns.js'
import { clientService } from '@/api/modules/clients.js'
import authService from '@/helpers/authservice/authService.js'
import WixtarPanel from './WixtarPanel.vue'
const DEFAULT_FIELDS = [
  { key: 'name',               label: '姓名',    visible: true,  required: true,  locked: false, note: '', allowEdit: true  },
  { key: 'phone',              label: '手機',    visible: true,  required: true,  locked: true,  note: '' },
  { key: 'email',              label: 'Email',   visible: true,  required: false, locked: false, note: '', allowEdit: true  },
  { key: 'birthday',           label: '生日',    visible: true,  required: false, locked: false, note: '', allowChangeOnce: false },
  { key: 'gender',             label: '性別',    visible: true,  required: false, locked: false, note: '', allowEdit: true  },
  { key: 'address_city',       label: '城市',    visible: true,  required: false, locked: false, note: '', allowEdit: true  },
  { key: 'address_district',   label: '區域',    visible: true,  required: false, locked: false, note: '', allowEdit: true  },
  { key: 'address_street',     label: '街道地址', visible: true,  required: false, locked: false, note: '', allowEdit: true  },
  { key: 'address_postal_code',label: '郵遞區號', visible: false, required: false, locked: false, note: '', allowEdit: true  },
  { key: 'password_login',     label: '密碼登入', visible: false, required: false, locked: false, note: '' },
]

const MALL_DEFAULT_FIELDS = DEFAULT_FIELDS.map(field => (
  field.key === 'password_login'
    ? { ...field, visible: true, required: true, locked: true }
    : { ...field }
))

export default {
  name: 'ClientSettings',

  components: {
    WixtarPanel,
  },

  setup() {
    const route = useRoute()
    const validTabs = ['member_fields', 'mall_member_fields', 'appearance', 'liff_urls', 'line', 'email', 'ai_creative', 'member_tier', 'wixtar', 'sms']
    const initialTab = validTabs.includes(route.query.tab) ? route.query.tab : 'member_fields'
    const activeTab = ref(initialTab)
    const commerceEnabled = ref(false)
    const allTabs = [
      { key: 'member_fields',    label: '會員註冊',   icon: 'ri-contacts-line' },
      { key: 'mall_member_fields', label: '商城會員註冊', icon: 'ri-store-2-line' },
      { key: 'appearance',       label: '外觀設定',   icon: 'ri-palette-line' },
      { key: 'liff_urls',        label: 'LIFF URLs', icon: 'ri-links-line' },
      { key: 'line',             label: 'LINE 設定',  icon: 'ri-line-line' },
      { key: 'ai_creative',      label: 'AI 創意',    icon: 'ri-magic-line' },
      { key: 'email',            label: '通知設定',   icon: 'ri-notification-3-line' },
      { key: 'member_tier',      label: '會員等級',   icon: 'ri-vip-crown-line' },
      { key: 'wixtar',           label: 'Wixtar 整合', icon: 'ri-terminal-box-line' },
      { key: 'sms',              label: '簡訊點數',   icon: 'ri-message-2-line' },
    ]

    const tabs = computed(() => allTabs.filter(tab => tab.key !== 'mall_member_fields' || commerceEnabled.value))
    const isMemberFieldsTab = computed(() => activeTab.value === 'member_fields' || (activeTab.value === 'mall_member_fields' && commerceEnabled.value))
    const loadCommerceAccess = async () => {
      try {
        const res = await clientSettingsService.getCommerceAccess()
        commerceEnabled.value = res?.data?.enabled === true
      } catch {
        commerceEnabled.value = false
      }
      if (activeTab.value === 'mall_member_fields') {
        if (commerceEnabled.value) {
          loadSettings(NAMESPACES.MALL_MEMBER_FIELDS)
          loadRegSettings(NAMESPACES.MALL_REGISTRATION)
        } else activeTab.value = 'member_fields'
      }
    }

    const baseLiffRoutes = [
      { key: 'member',          suffix: '/member',          label: '會員中心',     icon: 'ri-home-line' },
      { key: 'qrcode',          suffix: '/qrcode',          label: '會員 QR Code', icon: 'ri-qr-code-line' },
      { key: 'coupons',         suffix: '/coupons',         label: '我的優惠券',   icon: 'ri-coupon-line' },
      { key: 'points',          suffix: '/points',          label: '點數記錄',     icon: 'ri-coin-line' },
      { key: 'stamps',          suffix: '/stamps',          label: '集章活動',     icon: 'ri-stamp-line' },
      { key: 'profile',         suffix: '/profile',         label: '會員資料',     icon: 'ri-user-line' },
      { key: 'orders',          suffix: '/orders',          label: '交易紀錄',     icon: 'ri-shopping-bag-line' },
      { key: 'invoice-carrier', suffix: '/invoice-carrier', label: '電子發票載具', icon: 'ri-file-text-line' },
    ]

    const shareRewardRoute = (config) => ({
      key: 'share-reward',
      suffix: '/share-reward',
      label: '分享換好康',
      icon: 'ri-share-forward-line',
      hint: config?.shareRewardCampaignName
        ? `開啟後進入目前啟用的活動：${config.shareRewardCampaignName}`
        : '開啟後進入目前啟用中的活動。尚未啟用時，會員會看到目前沒有進行中的活動'
    })

    const routesFor = (config) => {
      const routes = [...baseLiffRoutes]
      if (clientMemberSystemType.value === 'WIXTAR') {
        routes.push({
          key: 'invite',
          suffix: '/invite',
          label: '邀請好友贏獎勵',
          icon: 'ri-user-shared-line',
          hint: '開啟邀請頁，會員可分享自己的推薦碼'
        })
      }
      routes.push(shareRewardRoute(config))
      return routes
    }

    const fieldStates = reactive(Object.fromEntries(
      [NAMESPACES.MEMBER_FIELDS, NAMESPACES.MALL_MEMBER_FIELDS].map(namespace => [namespace, {
        fields: (namespace === NAMESPACES.MALL_MEMBER_FIELDS ? MALL_DEFAULT_FIELDS : DEFAULT_FIELDS).map(f => ({ ...f })), loading: false, saving: false,
        loaded: false, saveSuccess: false, saveError: '',
      }])
    ))
    const currentFieldsNamespace = computed(() => activeTab.value === 'mall_member_fields' ? NAMESPACES.MALL_MEMBER_FIELDS : NAMESPACES.MEMBER_FIELDS)
    const fieldStateValue = key => computed({
      get: () => fieldStates[currentFieldsNamespace.value][key],
      set: value => { fieldStates[currentFieldsNamespace.value][key] = value },
    })
    const loading = fieldStateValue('loading')
    const saving = fieldStateValue('saving')
    const saveSuccess = fieldStateValue('saveSuccess')
    const saveError = fieldStateValue('saveError')
    const fieldsLoaded = fieldStateValue('loaded')
    const memberFields = fieldStateValue('fields')

    const mergeWithDefaults = (savedFields, defaults = DEFAULT_FIELDS) => {
      return defaults.map(def => {
        const saved = savedFields.find(f => f.key === def.key)
        if (!saved) return { ...def }
        const merged = {
          ...def,
          visible:  def.locked ? def.visible  : (saved.visible  ?? def.visible),
          required: def.locked ? def.required : (saved.required ?? def.required),
          note:     saved.note ?? '',
        }
        if ('allowChangeOnce' in def) {
          merged.allowChangeOnce = saved.allowChangeOnce ?? def.allowChangeOnce
        }
        if ('allowEdit' in def) {
          merged.allowEdit = saved.allowEdit ?? def.allowEdit
        }
        return merged
      })
    }

    const loadSettings = async (namespace = NAMESPACES.MEMBER_FIELDS) => {
      const state = fieldStates[namespace]
      if (state.loaded || state.loading) return
      state.loading = true
      state.saveError = ''
      try {
        const res = await clientSettingsService.get(namespace)
        const savedFields = res?.data?.settings?.fields ?? []
        const defaults = namespace === NAMESPACES.MALL_MEMBER_FIELDS ? MALL_DEFAULT_FIELDS : DEFAULT_FIELDS
        state.fields = mergeWithDefaults(savedFields, defaults)
        state.loaded = true
      } catch (e) {
        state.saveError = e?.response?.data?.message ?? '載入失敗，請重新整理後再試'
        if (namespace === NAMESPACES.MALL_MEMBER_FIELDS && e?.response?.status === 403) {
          commerceEnabled.value = false
          activeTab.value = 'member_fields'
        }
      } finally {
        state.loading = false
      }
    }

    const onVisibleChange = (field) => {
      if (!field.visible) {
        field.required = false
      }
    }

    const createRegistrationState = () => ({
      settings: { requireSmsVerification: false, requirePrivacyConsent: true },
      loading: false,
      saving: false,
      loaded: false,
      saveSuccess: false,
      saveError: '',
    })
    const registrationStates = reactive({
      [NAMESPACES.REGISTRATION]: createRegistrationState(),
      [NAMESPACES.MALL_REGISTRATION]: createRegistrationState(),
    })
    const currentRegistrationNamespace = computed(() => (
      activeTab.value === 'mall_member_fields' ? NAMESPACES.MALL_REGISTRATION : NAMESPACES.REGISTRATION
    ))
    const activeRegistration = computed(() => registrationStates[currentRegistrationNamespace.value])
    const registrationCopy = computed(() => (
      activeTab.value === 'mall_member_fields'
        ? {
            title: '商城會員註冊設定',
            desc: '設定商城註冊流程的驗證方式',
            sms: '啟用後，商城會員填完資料後需完成手機簡訊驗證才能完成註冊',
            privacy: '啟用後，商城註冊表單會顯示隱私條款與使用條款的合併同意勾選框；關閉則不要求勾選',
          }
        : {
            title: '會員註冊設定',
            desc: '設定 LINE LIFF 會員註冊流程的驗證方式',
            sms: '啟用後，不論新會員註冊或既有會員綁定 LINE，輸入手機號碼後皆需先完成簡訊驗證才能繼續',
            privacy: '啟用後，會員填完資料後需閱讀並同意隱私權政策與服務條款才能完成註冊；關閉則跳過此步驟直接完成',
          }
    ))

    const loadRegSettings = async (namespace = NAMESPACES.REGISTRATION) => {
      const state = registrationStates[namespace]
      if (state.loaded || state.loading) return
      state.loading = true
      state.saveError = ''
      try {
        const res = await clientSettingsService.get(namespace)
        const saved = res?.data?.settings ?? {}
        state.settings = { requireSmsVerification: false, requirePrivacyConsent: true, ...saved }
        state.loaded = true
      } catch (e) {
        state.saveError = e?.response?.data?.message ?? '載入失敗，請重新整理後再試'
        if (namespace === NAMESPACES.MALL_REGISTRATION && e?.response?.status === 403) {
          commerceEnabled.value = false
          activeTab.value = 'member_fields'
        }
      } finally {
        state.loading = false
      }
    }

    const handleSave = async () => {
      const namespace = currentFieldsNamespace.value
      const state = fieldStates[namespace]
      const regNamespace = currentRegistrationNamespace.value
      const regState = registrationStates[regNamespace]
      if (!state.loaded || !regState.loaded || state.saving || regState.saving) return
      state.saving = true
      regState.saving = true
      state.saveSuccess = false
      regState.saveSuccess = false
      state.saveError = ''
      regState.saveError = ''
      let fieldsSaved = false
      try {
        await clientSettingsService.set(namespace, {
          fields: state.fields.map((f) => {
            const item = { key: f.key, label: f.label, visible: f.visible, required: f.required, locked: f.locked, note: f.note ?? '' }
            if ('allowChangeOnce' in f) item.allowChangeOnce = f.allowChangeOnce ?? false
            if ('allowEdit' in f)       item.allowEdit = f.allowEdit
            return item
          }),
        })
        fieldsSaved = true
        await clientSettingsService.set(regNamespace, {
          requireSmsVerification: regState.settings.requireSmsVerification === true,
          requirePrivacyConsent: regState.settings.requirePrivacyConsent !== false,
        })
        state.saveSuccess = true
        regState.saveSuccess = true
        setTimeout(() => {
          state.saveSuccess = false
          regState.saveSuccess = false
        }, 3000)
      } catch (e) {
        const message = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
        if (fieldsSaved) regState.saveError = message
        else state.saveError = message
      } finally {
        state.saving = false
        regState.saving = false
      }
    }

    // 外觀設定
    const styleLoading     = ref(false)
    const styleSaving      = ref(false)
    const styleSaveSuccess = ref(false)
    const styleSaveError   = ref('')
    const NAV_ITEM_ICONS = {
      orders:       'ri-file-list-line',
      qrcode:       'ri-qr-code-line',
      stamps:       'ri-stamp-line',
      pointsDetail: 'ri-coin-line',
      invoice:      'ri-file-text-line',
      profile:      'ri-user-line',
      privacy:      'ri-shield-line',
      invite:       'ri-gift-line',
      shareReward:  'ri-share-forward-line',
    }

    const PLACEMENT_OPTS = [
      { value: 'icon',   label: '功能按鈕' },
      { value: 'menu',   label: '選單項目' },
      { value: 'hidden', label: '隱藏' },
    ]

    // 拖拉排序狀態
    const dragSrcIdx  = ref(-1)
    const dragOverIdx = ref(-1)

    const onDragStart = (idx) => { dragSrcIdx.value = idx }
    const onDragOver  = (idx) => { dragOverIdx.value = idx }
    const onDragEnd   = () => { dragOverIdx.value = -1; dragSrcIdx.value = -1 }
    const onDrop      = (toIdx) => {
      const from = dragSrcIdx.value
      if (from === toIdx || from === -1) return
      const items = [...styleSettings.value.navItems]
      const [moved] = items.splice(from, 1)
      items.splice(toIdx, 0, moved)
      styleSettings.value.navItems = items
      dragOverIdx.value = -1
      dragSrcIdx.value  = -1
    }

    const STYLE_DEFAULTS = {
      pageBg:            '#ffffff',
      brandColor:        '#1a1a1a',
      brandTextColor:    '#ffffff',
      accentColor:       '#f5a623',
      accentTextColor:   '#ffffff',
      navBg:             '#ffffff',
      navInactiveColor:  '#b0b0b0',
      avatarBg:          '#e8e8e8',
      avatarIconColor:   '#bdbdbd',
      profileNameColor:  '#1a1a1a',
      profileCodeColor:  '#888888',
      showPoints:       true,
      pointsLabel:      '紅利點數',
      pointsBg:         '#1a1a1a',
      pointsText:       '#ffffff',
      showCoupons:      true,
      couponsLabel:     '優惠票券',
      couponsBg:        '#f5a623',
      couponsText:      '#ffffff',
      showStamps:       true,
      stampsLabel:      '集章數',
      stampsBg:         '#ffffff',
      stampsText:       '#222222',
      navItems: [
        { key: 'orders',       label: '消費紀錄',           placement: 'icon' },
        { key: 'qrcode',       label: '會員條碼',           placement: 'icon' },
        { key: 'stamps',       label: '集章活動',           placement: 'icon' },
        { key: 'pointsDetail', label: '點數明細',           placement: 'icon' },
        { key: 'invoice',      label: '發票載具',           placement: 'icon' },
        { key: 'profile',      label: '會員資料',           placement: 'menu' },
        { key: 'privacy',      label: '會員隱私與權益說明', placement: 'menu' },
        { key: 'invite',       label: '邀請好友贏獎勵',     placement: 'menu' },
        { key: 'shareReward',  label: '分享換好康',         placement: 'menu' },
      ],
      iconsBg:          '#f5f5f5',
      iconsText:        '#333333',
      menuIconBg:       '#f5f5f5',
      menuText:         '#333333',
    }
    const styleSettings = ref({ ...STYLE_DEFAULTS })

    const mergeNavItems = (savedItems) => {
      const defaults = STYLE_DEFAULTS.navItems.map(item => ({ ...item }))
      if (!Array.isArray(savedItems) || savedItems.length === 0) return defaults
      const items = savedItems.map(item => ({ ...item }))
      if (!items.some(item => item.key === 'shareReward')) {
        const inviteIdx = items.findIndex(item => item.key === 'invite')
        const insertAt = inviteIdx >= 0 ? inviteIdx + 1 : items.length
        items.splice(insertAt, 0, { key: 'shareReward', label: '分享換好康', placement: 'menu' })
      }
      return items
    }

    const loadStyleSettings = async () => {
      styleLoading.value = true
      try {
        const res = await clientSettingsService.get(NAMESPACES.MEMBER_HOME_STYLE)
        const saved = res?.data?.settings ?? {}
        // navItems 用 saved 覆蓋（整個陣列），其他欄位淺合併
        const merged = { ...STYLE_DEFAULTS, ...saved }
        merged.navItems = mergeNavItems(merged.navItems)
        styleSettings.value = merged
      } catch {
        styleSettings.value = { ...STYLE_DEFAULTS, navItems: STYLE_DEFAULTS.navItems.map(i => ({ ...i })) }
      } finally {
        styleLoading.value = false
      }
    }

    const handleStyleSave = async () => {
      styleSaving.value      = true
      styleSaveSuccess.value = false
      styleSaveError.value   = ''
      try {
        await clientSettingsService.set(NAMESPACES.MEMBER_HOME_STYLE, styleSettings.value)
        styleSaveSuccess.value = true
        setTimeout(() => { styleSaveSuccess.value = false }, 3000)
      } catch (e) {
        styleSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        styleSaving.value = false
      }
    }

    const handleStyleReset = async () => {
      if (!window.confirm('確定要將所有外觀設定恢復為預設值嗎？')) return
      styleSaving.value      = true
      styleSaveSuccess.value = false
      styleSaveError.value   = ''
      try {
        styleSettings.value = { ...STYLE_DEFAULTS }
        await clientSettingsService.set(NAMESPACES.MEMBER_HOME_STYLE, STYLE_DEFAULTS)
        styleSaveSuccess.value = true
        setTimeout(() => { styleSaveSuccess.value = false }, 3000)
      } catch (e) {
        styleSaveError.value = e?.response?.data?.message ?? '恢復失敗，請稍後再試'
      } finally {
        styleSaving.value = false
      }
    }

    // LINE 設定
    const lineLoading     = ref(false)
    const lineSaving      = ref(false)
    const lineSaveSuccess = ref(false)
    const lineSaveError   = ref('')
    const lineSettings    = ref({ richMenuMode: 'schedule' })

    const loadLineSettings = async () => {
      lineLoading.value = true
      try {
        const res = await clientSettingsService.get(NAMESPACES.LINE)
        const saved = res?.data?.settings ?? {}
        lineSettings.value = { richMenuMode: 'schedule', crossOaBindingMode: 'phone_verification', ...saved }
      } catch {
        lineSettings.value = { richMenuMode: 'schedule', crossOaBindingMode: 'phone_verification' }
      } finally {
        lineLoading.value = false
      }
    }

    const handleLineSave = async () => {
      lineSaving.value      = true
      lineSaveSuccess.value = false
      lineSaveError.value   = ''
      try {
        await clientSettingsService.set(NAMESPACES.LINE, lineSettings.value)
        lineSaveSuccess.value = true
        setTimeout(() => { lineSaveSuccess.value = false }, 3000)
      } catch (e) {
        lineSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        lineSaving.value = false
      }
    }

    // AI 創意設定
    const AI_CREATIVE_DEFAULTS = {
      brandName: '',
      brandColor: '',
      brandSecondaryColor: '',
      syncFromMemberHomeStyle: true,
    }
    const aiCreativeLoading     = ref(false)
    const aiCreativeSaving      = ref(false)
    const aiCreativeSaveSuccess = ref(false)
    const aiCreativeSaveError   = ref('')
    const aiCreativeSettings    = ref({ ...AI_CREATIVE_DEFAULTS })
    const aiCreativeQuota       = ref(null)
    const aiTopupPackages       = ref([])
    const aiTopupOrders         = ref([])
    const aiTopupProcessing     = ref(false)
    const aiTopupFormRef        = ref(null)
    const aiTopupPaymentUrl     = ref('')
    const aiTopupOrderData      = reactive({})
    const isDevMode             = process.env.NODE_ENV !== 'production'

    const formatDateTime = (value) => {
      if (!value) return '-'
      return new Date(value).toLocaleString('zh-TW')
    }

    const formatTopupStatus = (status) => {
      const map = {
        pending: '待付款',
        paid: '已付款',
        failed: '付款失敗',
        cancelled: '已取消',
      }
      return map[status] || status || '未知'
    }

    const isValidHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(value || '')

    const normalizeColorForInput = (value, key) => {
      if (isValidHexColor(value)) {
        return value
      }

      // 同步會員中心外觀時，色票優先顯示外觀設定對應色
      if (aiCreativeSettings.value?.syncFromMemberHomeStyle) {
        const syncedColor = key === 'brandSecondaryColor'
          ? styleSettings.value?.accentColor
          : styleSettings.value?.brandColor
        if (isValidHexColor(syncedColor)) {
          return syncedColor
        }
      }

      return key === 'brandSecondaryColor' ? '#f5a623' : '#1a1a1a'
    }

    const handleAiCreativeColorInput = (key, value) => {
      if (!aiCreativeSettings.value || !Object.prototype.hasOwnProperty.call(aiCreativeSettings.value, key)) {
        return
      }
      aiCreativeSettings.value[key] = value
    }

    const loadAiCreativeSettings = async () => {
      aiCreativeLoading.value = true
      try {
        const [settingsRes, quotaRes, packageRes, topupOrderRes] = await Promise.all([
          clientSettingsService.get(NAMESPACES.AI_CREATIVE),
          aiCreativeService.getQuota().catch(() => null),
          aiCreativeService.listTopupPackages().catch(() => null),
          aiCreativeService.listTopupOrders({ page: 1, pageSize: 10 }).catch(() => null),
        ])
        const saved = settingsRes?.data?.settings ?? {}
        aiCreativeSettings.value = { ...AI_CREATIVE_DEFAULTS, ...saved }
        aiCreativeQuota.value = quotaRes?.data ?? null
        aiTopupPackages.value = packageRes?.data?.packages ?? []
        aiTopupOrders.value = topupOrderRes?.data?.items ?? []
      } catch {
        aiCreativeSettings.value = { ...AI_CREATIVE_DEFAULTS }
        aiTopupPackages.value = []
        aiTopupOrders.value = []
      } finally {
        aiCreativeLoading.value = false
      }
    }

    const handleAiCreativeTopup = async (packageCode) => {
      if (!packageCode) return
      aiTopupProcessing.value = true
      try {
        const res = await aiCreativeService.createTopupOrder(packageCode)
        const data = res?.data || {}
        aiTopupPaymentUrl.value = data.paymentUrl || ''
        Object.keys(aiTopupOrderData).forEach((key) => delete aiTopupOrderData[key])
        Object.assign(aiTopupOrderData, data.orderData || {})
        if (aiTopupPaymentUrl.value && Object.keys(aiTopupOrderData).length > 0) {
          setTimeout(() => {
            aiTopupFormRef.value?.submit()
          }, 200)
        }
      } catch (e) {
        aiCreativeSaveError.value = e?.message || '建立儲值訂單失敗，請稍後再試'
      } finally {
        aiTopupProcessing.value = false
      }
    }

    const handleAiCreativeDevComplete = async (orderId) => {
      if (!orderId) return
      aiTopupProcessing.value = true
      try {
        await aiCreativeService.devCompleteTopupOrder(orderId)
        await loadAiCreativeSettings()
      } catch (e) {
        aiCreativeSaveError.value = e?.message || 'DEV 模擬失敗'
      } finally {
        aiTopupProcessing.value = false
      }
    }

    const handleAiCreativeSave = async () => {
      aiCreativeSaving.value      = true
      aiCreativeSaveSuccess.value = false
      aiCreativeSaveError.value   = ''
      try {
        await clientSettingsService.set(NAMESPACES.AI_CREATIVE, aiCreativeSettings.value)
        aiCreativeSaveSuccess.value = true
        setTimeout(() => { aiCreativeSaveSuccess.value = false }, 3000)
      } catch (e) {
        aiCreativeSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        aiCreativeSaving.value = false
      }
    }

    // 通知收件 Email（billing_contact_email，最多 5 個以逗號分隔）
    const notificationEmail        = ref('')
    const notificationEmailSaving  = ref(false)
    const notificationEmailSuccess = ref(false)
    const notificationEmailError   = ref('')

    // 即時解析目前輸入的 email 清單（用於預覽 tag）
    const notificationEmailPreview = computed(() =>
      notificationEmail.value
        ? notificationEmail.value.split(',').map(e => e.trim()).filter(e => e.length > 0)
        : []
    )

    const loadNotificationEmail = async () => {
      try {
        const res = await clientSettingsService.getNotificationEmail()
        const emails = res?.data?.data?.notificationEmails ?? []
        notificationEmail.value = Array.isArray(emails) ? emails.join(', ') : ''
      } catch {
        notificationEmail.value = ''
      }
    }

    const handleNotificationEmailSave = async () => {
      notificationEmailSaving.value  = true
      notificationEmailSuccess.value = false
      notificationEmailError.value   = ''
      try {
        await clientSettingsService.setNotificationEmail(notificationEmail.value || null)
        notificationEmailSuccess.value = true
        setTimeout(() => { notificationEmailSuccess.value = false }, 3000)
      } catch (e) {
        notificationEmailError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        notificationEmailSaving.value = false
      }
    }

    // AI 客服通知 Email（client_settings.chatmate.notifyEmails，最多 5 個）
    const aiNotifyEmail        = ref('')
    const aiNotifyEmailSaving  = ref(false)
    const aiNotifyEmailSuccess = ref(false)
    const aiNotifyEmailError   = ref('')

    const aiNotifyEmailPreview = computed(() =>
      aiNotifyEmail.value
        ? aiNotifyEmail.value.split(',').map(e => e.trim()).filter(e => e.length > 0)
        : []
    )

    const loadAiNotifyEmail = async () => {
      try {
        const res = await clientSettingsService.get(NAMESPACES.CHATMATE)
        const s = res?.data?.settings ?? {}
        const raw = s.notifyEmails ?? ''
        aiNotifyEmail.value = typeof raw === 'string' && raw.length > 0
          ? raw.split(',').map(e => e.trim()).filter(Boolean).join(', ')
          : ''
      } catch {
        aiNotifyEmail.value = ''
      }
    }

    const handleAiNotifyEmailSave = async () => {
      aiNotifyEmailSaving.value  = true
      aiNotifyEmailSuccess.value = false
      aiNotifyEmailError.value   = ''

      const emails = aiNotifyEmailPreview.value
      if (emails.length > 5) {
        aiNotifyEmailError.value = '最多可設定 5 個收件 Email'
        aiNotifyEmailSaving.value = false
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const invalid = emails.filter(e => !emailRegex.test(e))
      if (invalid.length > 0) {
        aiNotifyEmailError.value = `格式不正確的 Email：${invalid.join(', ')}`
        aiNotifyEmailSaving.value = false
        return
      }

      try {
        await clientSettingsService.merge(NAMESPACES.CHATMATE, {
          notifyEmails: emails.length > 0 ? emails.join(',') : '',
        })
        aiNotifyEmailSuccess.value = true
        setTimeout(() => { aiNotifyEmailSuccess.value = false }, 3000)
      } catch (e) {
        aiNotifyEmailError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        aiNotifyEmailSaving.value = false
      }
    }

    // 優惠券到期提醒設定
    const couponSettings    = ref({ expiryReminderEnabled: false, expiryReminderDays: 3 })
    const couponSaving      = ref(false)
    const couponSaveSuccess = ref(false)
    const couponSaveError   = ref('')

    const loadCouponSettings = async () => {
      try {
        const res = await clientSettingsService.get(NAMESPACES.COUPONS)
        const s = res?.data?.settings ?? {}
        couponSettings.value = {
          expiryReminderEnabled: s.expiryReminderEnabled ?? false,
          expiryReminderDays:    s.expiryReminderDays    ?? 3,
        }
      } catch {
        couponSettings.value = { expiryReminderEnabled: false, expiryReminderDays: 3 }
      }
    }

    const handleCouponSave = async () => {
      couponSaving.value      = true
      couponSaveSuccess.value = false
      couponSaveError.value   = ''
      try {
        await clientSettingsService.set(NAMESPACES.COUPONS, couponSettings.value)
        couponSaveSuccess.value = true
        setTimeout(() => { couponSaveSuccess.value = false }, 3000)
      } catch (e) {
        couponSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        couponSaving.value = false
      }
    }

    // LIFF URLs（依客戶 memberSystemType 使用 /lockline 或 /wixtar，與 LINE 設定頁一致）
    const liffLoading = ref(false)
    const liffEntries = ref([])
    const copiedId = ref('')
    const clientMemberSystemType = ref('LOCKLINE')

    const liffPathPrefix = computed(() =>
      clientMemberSystemType.value === 'WIXTAR' ? '/wixtar' : '/lockline'
    )

    const memberSystemLabel = computed(() =>
      clientMemberSystemType.value === 'WIXTAR' ? 'Wixtar 會員（/wixtar）' : 'Lockline 會員（/lockline）'
    )

    const getLiffBaseUrl = () => {
      if (process.env.VUE_APP_LIFF_BASE_URL) {
        return process.env.VUE_APP_LIFF_BASE_URL
      }
      if (process.env.NODE_ENV === 'development') {
        return 'https://localhost:8082'
      }
      const origin = window.location.origin
      // 必須先判斷 dev-admin，避免 substring 誤把 admin.lockline.io 套到正式 liff
      if (origin.includes('dev-admin.lockline.io')) {
        return 'https://dev-liff.lockline.io'
      }
      if (origin.includes('admin.lockline.io')) {
        return 'https://liff.lockline.io'
      }
      return 'https://liff.lockline.io'
    }

    const buildLineLoginCallbackUrl = (channelId) => {
      if (!channelId) return ''
      const base = getLiffBaseUrl().replace(/\/$/, '')
      return `${base}${liffPathPrefix.value}/linelogin/${channelId}`
    }

    /** LINE Console「LIFF Endpoint URL」：自有網域 + 前綴 + lineliff + 頻道 ID（不含 liff.line.me） */
    const buildLiffEndpointUrl = (channelId) => {
      if (!channelId) return ''
      const base = getLiffBaseUrl().replace(/\/$/, '')
      return `${base}${liffPathPrefix.value}/lineliff/${channelId}`
    }

    // 有在 LINE 設定存過就用存檔（tunnel、正式網域）；沒存才用 VUE_APP_LIFF_BASE_URL 推算
    const resolveLiffEndpointUrl = (config) => {
      const stored = typeof config?.commonLiffEndpointUrl === 'string' ? config.commonLiffEndpointUrl.trim() : ''
      if (stored) return stored
      return buildLiffEndpointUrl(config?.channelId)
    }

    const resolveLineLoginCallbackUrl = (config) => {
      const stored = typeof config?.loginCallbackUrl === 'string' ? config.loginCallbackUrl.trim() : ''
      if (stored) return stored
      return buildLineLoginCallbackUrl(config?.channelId)
    }

    // liff.line.me 後面只接 suffix（例如 /member），不可再帶 /lockline/lineliff/{channelId}
    // 因為 LIFF SDK 會把這段（liff.state）接到 Endpoint URL（已含 /lockline/lineliff/{channelId}）後面，
    // 重複帶會變成 .../lineliff/{channelId}/lockline/lineliff/{channelId}/member 而比對不到路由 → 404
    const buildLiffOpenUrl = (config, suffix) => {
      if (!config?.commonLiffId) return ''
      return `https://liff.line.me/${config.commonLiffId}${suffix}`
    }

    const copyKeyEndpoint = (config) => `ep-${config.configId}`
    const copyKeyCallback = (config) => `cb-${config.configId}`
    const copyKeyLiffRoute = (config, suffix) => `liff-${config.configId}-${suffix}`

    const loadClientMemberSystemType = async () => {
      try {
        const user = authService.getCurrentUser()
        const clientId = user?.clientId
        if (!clientId) {
          clientMemberSystemType.value = 'LOCKLINE'
          return
        }
        const response = await clientService.getClient(clientId)
        if (response && response.success && response.data?.clientData) {
          clientMemberSystemType.value = response.data.clientData.memberSystemType === 'WIXTAR' ? 'WIXTAR' : 'LOCKLINE'
        } else {
          clientMemberSystemType.value = 'LOCKLINE'
        }
      } catch {
        clientMemberSystemType.value = 'LOCKLINE'
      }
    }

    const loadLiffUrls = async () => {
      liffLoading.value = true
      try {
        await loadClientMemberSystemType()
        const res = await lineConfigApi.list()
        const items = res?.data?.data?.items ?? res?.data?.items ?? []
        const campaignsByLineConfig = new Map()
        try {
          const campaignRes = await shareRewardCampaignService.getCampaigns({
            isActive: true,
            page: 1,
            pageSize: 100
          })
          const campaigns = campaignRes?.data?.items || []
          campaigns.forEach((campaign) => {
            if (campaign?.lineConfigId && campaign.isActive) {
              campaignsByLineConfig.set(campaign.lineConfigId, campaign)
            }
          })
        } catch {
          campaignsByLineConfig.clear()
        }
        liffEntries.value = items.map(cfg => {
          const campaign = campaignsByLineConfig.get(cfg.id)
          const ids = Array.isArray(cfg.liffIds) ? cfg.liffIds : []
          // LINE 列表 API 的 Channel ID 在 messagingApi.channelId（與 follow-marketing 一致）
          const channelId = cfg.messagingApi?.channelId || cfg.messagingChannelId || ''
          return {
            configId:      cfg.id,
            configName:    cfg.configName,
            channelId,
            isActive:      cfg.isActive,
            // liff_ids[0] = 共用入口，liff_ids[1] = 電商模組專用（約定俗成）
            commonLiffId:  ids[0] ?? null,
            commonLiffEndpointUrl: typeof cfg.commonLiffEndpointUrl === 'string' ? cfg.commonLiffEndpointUrl.trim() : '',
            loginCallbackUrl: cfg.lineLogin?.callbackUrl || cfg.loginCallbackUrl || '',
            shareRewardCampaignId: campaign?.id || '',
            shareRewardCampaignName: campaign?.campaignName || '',
          }
        })
      } catch {
        liffEntries.value = []
      } finally {
        liffLoading.value = false
      }
    }

    const copyUrl = async (fullUrl, key) => {
      if (!fullUrl || fullUrl === '#') return
      try {
        await navigator.clipboard.writeText(fullUrl)
      } catch {
        const el = document.createElement('textarea')
        el.value = fullUrl
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      copiedId.value = key
      setTimeout(() => { if (copiedId.value === key) copiedId.value = '' }, 2000)
    }

    // 會員等級計算模式設定
    const memberTierLoading = ref(false)
    const memberTierSaving = ref(false)
    const memberTierSaveSuccess = ref(false)
    const memberTierSaveError = ref('')
    const memberTierSettings = reactive({
      calculationMode: 'lifetime',
      annualDowngradeEnabled: false,
      annualDowngradeMonth: 1,
    })

    const loadMemberTierSettings = async () => {
      memberTierLoading.value = true
      try {
        const res = await clientSettingsService.get(NAMESPACES.MEMBER_TIER)
        const s = res?.data?.settings ?? {}
        memberTierSettings.calculationMode = s.calculationMode ?? 'lifetime'
        memberTierSettings.annualDowngradeEnabled = s.annualDowngradeEnabled ?? false
        memberTierSettings.annualDowngradeMonth = s.annualDowngradeMonth ?? 1
      } catch {
        memberTierSettings.calculationMode = 'lifetime'
        memberTierSettings.annualDowngradeEnabled = false
        memberTierSettings.annualDowngradeMonth = 1
      } finally {
        memberTierLoading.value = false
      }
    }

    const handleMemberTierSave = async () => {
      memberTierSaving.value = true
      memberTierSaveSuccess.value = false
      memberTierSaveError.value = ''
      try {
        await clientSettingsService.set(NAMESPACES.MEMBER_TIER, { ...memberTierSettings })
        memberTierSaveSuccess.value = true
        setTimeout(() => { memberTierSaveSuccess.value = false }, 3000)
      } catch (e) {
        memberTierSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      } finally {
        memberTierSaving.value = false
      }
    }

    const smsLoading = ref(false)
    const smsSaving = ref(false)
    const smsMessage = ref('')
    const smsSummary = ref(null)
    const smsEntries = ref([])
    const smsThreshold = ref(100)
    const smsPagination = ref({ page: 1, pageSize: 20, total: 0 })
    const smsPageCount = computed(() => Math.max(1, Math.ceil((smsPagination.value.total || 0) / smsPagination.value.pageSize)))

    const formatSmsTime = (value) => {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString('zh-TW', { hour12: false })
    }

    const loadSmsPoints = async (page = 1) => {
      smsLoading.value = true
      smsMessage.value = ''
      try {
        const response = await clientSettingsService.getSmsPoints({ page, pageSize: 20 })
        const data = response?.data || {}
        smsSummary.value = data
        smsEntries.value = data.items || []
        smsThreshold.value = Number.isFinite(Number(data.threshold)) ? Number(data.threshold) : 100
        smsPagination.value = data.pagination || { page, pageSize: 20, total: 0 }
      } catch (e) {
        smsMessage.value = e?.response?.data?.message || '無法載入簡訊點數'
      } finally {
        smsLoading.value = false
      }
    }

    const saveSmsThreshold = async () => {
      const threshold = Number(smsThreshold.value)
      if (!Number.isInteger(threshold) || threshold < 0) {
        smsMessage.value = '門檻須為 0 以上的整數'
        return
      }
      smsSaving.value = true
      smsMessage.value = ''
      try {
        await clientSettingsService.setSmsThreshold(threshold)
        smsMessage.value = '低餘額門檻已儲存'
        await loadSmsPoints(smsPagination.value.page || 1)
      } catch (e) {
        smsMessage.value = e?.response?.data?.message || e?.response?.data?.error?.message || '儲存失敗'
      } finally {
        smsSaving.value = false
      }
    }

    watch(activeTab, (tab) => {
      if (tab === 'mall_member_fields' && commerceEnabled.value) {
        loadSettings(NAMESPACES.MALL_MEMBER_FIELDS)
        loadRegSettings(NAMESPACES.MALL_REGISTRATION)
      }
      if (tab === 'liff_urls') {
        loadLiffUrls()
      }
      if (tab === 'sms') {
        loadSmsPoints(1)
      }
    })

    onMounted(() => {
      loadCommerceAccess()
      loadSettings()
      loadRegSettings()
      loadStyleSettings()
      loadLiffUrls()
      loadLineSettings()
      loadAiCreativeSettings()
      loadNotificationEmail()
      loadAiNotifyEmail()
      loadCouponSettings()
      loadMemberTierSettings()
      if (activeTab.value === 'sms') loadSmsPoints(1)
    })

    return {
      activeTab, tabs, isMemberFieldsTab, fieldsLoaded,
      smsLoading, smsSaving, smsMessage, smsSummary, smsEntries, smsThreshold, smsPagination, smsPageCount,
      loadSmsPoints, saveSmsThreshold, formatSmsTime, smsEntryLabel, smsSourceLabel, formatSmsPoints,
      loading, saving, saveSuccess, saveError,
      memberFields,
      onVisibleChange,
      handleSave,
      activeRegistration,
      registrationCopy,
      styleLoading, styleSaving, styleSaveSuccess, styleSaveError,
      styleSettings,
      handleStyleSave,
      handleStyleReset,
      NAV_ITEM_ICONS,
      PLACEMENT_OPTS,
      dragOverIdx,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
      liffLoading, liffEntries, routesFor, copiedId,
      liffPathPrefix, memberSystemLabel,
      buildLiffOpenUrl, resolveLiffEndpointUrl, resolveLineLoginCallbackUrl,
      copyKeyEndpoint, copyKeyCallback, copyKeyLiffRoute,
      copyUrl,
      lineLoading, lineSaving, lineSaveSuccess, lineSaveError,
      lineSettings,
      handleLineSave,
      aiCreativeLoading, aiCreativeSaving, aiCreativeSaveSuccess, aiCreativeSaveError,
      aiCreativeSettings, aiCreativeQuota, handleAiCreativeSave,
      aiTopupPackages, aiTopupOrders, aiTopupProcessing,
      aiTopupFormRef, aiTopupPaymentUrl, aiTopupOrderData,
      handleAiCreativeTopup, handleAiCreativeDevComplete,
      normalizeColorForInput, handleAiCreativeColorInput,
      formatDateTime, formatTopupStatus, isDevMode,
      notificationEmail, notificationEmailPreview, notificationEmailSaving, notificationEmailSuccess, notificationEmailError,
      handleNotificationEmailSave,
      aiNotifyEmail, aiNotifyEmailPreview, aiNotifyEmailSaving, aiNotifyEmailSuccess, aiNotifyEmailError,
      handleAiNotifyEmailSave,
      couponSettings, couponSaving, couponSaveSuccess, couponSaveError,
      handleCouponSave,
      memberTierLoading, memberTierSaving, memberTierSaveSuccess, memberTierSaveError,
      memberTierSettings,
      handleMemberTierSave,
      clientMemberSystemType,
    }
  },
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 左側欄 + 右側內容的雙欄布局 */
.settings-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.settings-sidebar {
  flex-shrink: 0;
  width: 180px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 8px;
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  border-radius: 8px;
  transition: all 0.15s;
  text-align: left;
  white-space: nowrap;
}

.sidebar-item:hover {
  color: #667eea;
  background: #f8f9ff;
}

.sidebar-item.active {
  color: #667eea;
  background: #eef0fd;
  font-weight: 600;
}

.sidebar-item i {
  font-size: 16px;
  flex-shrink: 0;
}

.settings-content {
  flex: 1;
  min-width: 0;
}

/* 手機版：改回橫向捲動 Tabs */
@media (max-width: 768px) {
  .settings-layout {
    flex-direction: column;
    gap: 16px;
  }

  .settings-sidebar {
    width: 100%;
    position: static;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    border-radius: 8px;
    padding: 6px;
    gap: 4px;
  }

  .settings-sidebar::-webkit-scrollbar {
    display: none;
  }

  .sidebar-item {
    flex-shrink: 0;
    padding: 8px 14px;
    border-radius: 6px;
  }

  .settings-content {
    width: 100%;
  }
}

/* 設定卡片 */
.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  max-width: 760px;
}

.settings-card-spaced {
  margin-top: 16px;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  color: #667eea;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* 欄位表格 */
.fields-table-wrapper {
  padding: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.fields-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
}

.fields-table thead th {
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  text-align: center;
}

.fields-table thead th.col-label {
  text-align: left;
}

.fields-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
}

.fields-table tbody tr:last-child {
  border-bottom: none;
}

.fields-table tbody tr:hover {
  background: #fafbff;
}

.fields-table tbody tr.is-locked {
  background: #fafafa;
}

.fields-table td {
  padding: 14px 20px;
  text-align: center;
  vertical-align: middle;
}

.fields-table td.col-label {
  text-align: left;
}

.col-toggle {
  width: 80px;
}

.col-note {
  min-width: 220px;
  text-align: left !important;
}

.note-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  background: #fff;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.note-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
}

.note-input::placeholder {
  color: #ced4da;
}

.note-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #adb5bd;
}

.note-hint i {
  font-size: 11px;
}

.note-hint.muted {
  color: #ced4da;
}

.col-extra {
  min-width: 160px;
  text-align: left !important;
}

.extra-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
  padding: 4px 0;
}

.extra-toggle-label {
  font-size: 13px;
  color: #495057;
  white-space: nowrap;
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
}

.lock-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 2px 8px;
  background: #e9ecef;
  color: #6c757d;
  font-size: 11px;
  border-radius: 10px;
}

.note-text {
  font-size: 12px;
  color: #6c757d;
}

.note-text.muted {
  color: #adb5bd;
}

/* Toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.slider {
  position: absolute;
  inset: 0;
  background: #dee2e6;
  border-radius: 24px;
  transition: background 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .slider {
  background: #667eea;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(20px);
}

/* 卡片底部 */
.settings-card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.settings-card-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  background: #fafafa;
}

.alert {
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  flex: 1;
}

.alert-danger {
  background: #fff5f5;
  color: #c53030;
  border: 1px solid #fed7d7;
}

.alert-success {
  background: #f0fff4;
  color: #276749;
  border: 1px solid #c6f6d5;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s;
}

.btn-primary {
  background: #667eea;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline-secondary {
  color: #6c757d;
  border: 1px solid #dee2e6;
  background: #fff;
}

.btn-outline-secondary:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.btn-outline-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #6c757d;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

.spinner-sm.spinner-sm-dark {
  border-color: rgba(108, 117, 125, 0.35);
  border-top-color: #6c757d;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* LIFF URLs */
.liff-card {
  margin-bottom: 16px;
  max-width: 760px;
}

.liff-card .settings-card-header > i {
  font-size: 28px;
  color: #06c755;
}

.badge-shared {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  border-radius: 10px;
  font-weight: 500;
}

.badge-inactive {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  background: #fee2e2;
  color: #c53030;
  font-size: 11px;
  border-radius: 10px;
  font-weight: 500;
}

.badge-member-mode {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 11px;
  border-radius: 10px;
  font-weight: 600;
}

.badge-member-mode.is-lockline {
  background: #e3f2fd;
  color: #1565c0;
}

.badge-member-mode.is-wixtar {
  background: #fff3e0;
  color: #e65100;
}

.liff-row-callback {
  background: #fafafa;
}

.liff-row-endpoint {
  background: #fafafa;
}

.liff-route-label-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.liff-route-label-stack .liff-route-label {
  white-space: normal;
}

.liff-route-hint {
  font-size: 11px;
  color: #868e96;
  line-height: 1.35;
  font-weight: 400;
}

.liff-row-endpoint .liff-route-meta,
.liff-row-callback .liff-route-meta {
  align-items: flex-start;
  width: auto;
  min-width: 200px;
  max-width: 38%;
}

.liff-empty {
  padding: 20px 24px;
  color: #adb5bd;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.liff-list {
  padding: 8px 0;
}

.liff-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.1s;
}

.liff-row:last-child {
  border-bottom: none;
}

.liff-row:hover {
  background: #fafbff;
}

.liff-type-badge {
  padding: 3px 10px;
  border-radius: 10px;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.liff-route-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 140px;
  flex-shrink: 0;
}

.liff-route-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f0f4ff;
  color: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.liff-route-label {
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
}

.liff-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.liff-id {
  font-size: 13px;
  font-weight: 600;
  color: #2d3748;
  font-family: monospace;
}

.liff-url {
  font-size: 12px;
  color: #667eea;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  word-break: break-all;
}

.liff-url:hover {
  text-decoration: underline;
}

.liff-url i {
  font-size: 11px;
  flex-shrink: 0;
}

.liff-url-missing {
  font-size: 12px;
  color: #9aa0a6;
}

.btn-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
  flex-shrink: 0;
  white-space: nowrap;
}

.btn-copy:hover {
  background: #f8f9ff;
  border-color: #667eea;
  color: #667eea;
}

.btn-copy.copied {
  background: #f0fff4;
  border-color: #38a169;
  color: #276749;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #adb5bd;
}

.empty-state i {
  font-size: 40px;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

/* 外觀設定 */
.appearance-body {
  padding: 8px 0;
}

.color-group {
  border-bottom: 1px solid #f0f0f0;
  padding: 20px 24px;

  &:last-child { border-bottom: none; }
}

.color-group-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}

.group-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.home-section-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 4px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  .home-section-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    white-space: nowrap;
    padding: 0 4px;
  }
}

.show-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-left: auto;
  flex-shrink: 0;

  input { display: none; }

  .slider {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
    background: #dee2e6;
    border-radius: 20px;
    transition: background 0.2s;
    flex-shrink: 0;

    &::before {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      left: 3px;
      top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
  }

  input:checked + .slider {
    background: #667eea;
    &::before { transform: translateX(16px); }
  }

  .show-toggle-label {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
    min-width: 24px;
  }
}

.color-group-desc {
  font-size: 13px;
  color: #6c757d;
  margin: -4px 0 12px;
}

.nav-items-config {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 12px;
  cursor: grab;
  transition: background 0.15s, box-shadow 0.15s;
  user-select: none;

  &:active { cursor: grabbing; }

  &.drag-over {
    background: #e8ecff;
    box-shadow: 0 0 0 2px #667eea;
  }
}

@media (max-width: 600px) {
  .nav-item-row {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  .nav-item-label {
    flex: 1;
    min-width: 0;
  }

  .nav-label-input {
    width: 80px;
  }

  .placement-selector {
    width: calc(100% - 40px);
    /* 與 icon 對齊：drag handle 約 28px + gap 12px */
    margin-left: 40px;
  }

  .placement-btn {
    flex: 1;
    text-align: center;
  }
}

.nav-drag-handle {
  color: #adb5bd;
  font-size: 16px;
  flex-shrink: 0;
  cursor: grab;
  line-height: 1;

  &:active { cursor: grabbing; }
}

.nav-item-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  color: #495057;
  flex-shrink: 0;

  i { font-size: 15px; color: #6c757d; }
}

.nav-label-input {
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 3px 8px;
  font-size: 13px;
  color: #495057;
  width: 120px;
  outline: none;
  transition: border-color 0.15s;

  &:focus { border-color: #667eea; }
}

.label-text-input {
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 13px;
  color: #495057;
  width: 140px;
  outline: none;
  transition: border-color 0.15s;

  &:focus { border-color: #667eea; }
}

.placement-selector {
  display: flex;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.placement-btn {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  border: none;
  border-right: 1px solid #dee2e6;
  cursor: pointer;
  color: #6c757d;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;

  &:last-child { border-right: none; }

  &:hover:not(.active) { background: #f0f0f0; }

  &.active {
    background: #667eea;
    color: #fff;
  }

  &.active:last-child {
    background: #e74c3c;
  }
}

.visibility-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}

.visibility-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 12px;
}

.visibility-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #495057;

  i {
    font-size: 16px;
    color: #6c757d;
  }
}

.settings-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.settings-header-actions .btn {
  padding: 8px 16px;
}

.stat-preview {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-sizing: border-box;
}

.color-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.color-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.color-row-label {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.color-row-name {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
}

.color-row-desc {
  font-size: 12px;
  color: #9ca3af;
}

.color-row-ctrl {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.color-input {
  width: 40px;
  height: 36px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 2px;
  cursor: pointer;
  background: none;
  flex-shrink: 0;
}

.hex-input {
  width: 90px;
  padding: 7px 10px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
  color: #495057;
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
  }
}

.reset-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.15s;

  &:hover {
    background: #f8f9ff;
    border-color: #667eea;
    color: #667eea;
  }
}

/* 會員註冊設定 */
.reg-settings-body {
  padding: 8px 0;
}

.reg-setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child { border-bottom: none; }
}

.reg-setting-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.reg-setting-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #2d3748;

  i { font-size: 16px; color: #667eea; }
}

.reg-setting-desc {
  font-size: 13px;
  color: #6c757d;
  line-height: 1.5;
}

/* LINE 圖文選單模式選擇 */
.rich-menu-mode-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mode-option {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover { background: #f8f9ff; border-color: #c5cdf8; }
  &.active { border-color: #667eea; background: #f0f3ff; }
}

.mode-radio {
  display: flex;
  align-items: center;
  padding-top: 2px;
  flex-shrink: 0;
}

.radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s;

  &.checked {
    border-color: #667eea;
    background: radial-gradient(circle, #667eea 6px, transparent 6px);
  }
}

.mode-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;

  &.schedule { background: #e8f0fe; color: #3b5bdb; }
  &.member   { background: #fff9e6; color: #d97706; }
}

.mode-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.mode-title {
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
}

.mode-desc {
  font-size: 13px;
  color: #6c757d;
  line-height: 1.5;
}

/* 跨 OA 共用模式唯讀顯示 */
.cross-oa-mode-display {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cross-oa-current-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cross-oa-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 6px 0;
}

.mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
}

.mode-badge.mode-auto {
  background: #e6f9ec;
  color: #1a7f3c;
  border: 1px solid #a7dfb8;
}

.mode-badge.mode-phone {
  background: #f0f3ff;
  color: #3b5bdb;
  border: 1px solid #c5cdf8;
}

.mode-description {
  font-size: 13px;
  color: #6c757d;
  line-height: 1.6;
  margin: 8px 0 0 0;
}

.cross-oa-other-modes {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.other-modes-title {
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.other-modes-title i {
  font-size: 15px;
  color: #9ca3af;
}

.other-mode-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.other-mode-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
  flex: 1;
  min-width: 200px;
}

.cross-oa-contact-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #92400e;
}

.cross-oa-contact-hint i {
  font-size: 16px;
  flex-shrink: 0;
  color: #d97706;
}

/* Coming soon */
.coming-soon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: #adb5bd;
}

.coming-soon i {
  font-size: 48px;
}

.coming-soon p {
  font-size: 15px;
  margin: 0;
}
.email-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.email-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #eaf0fb;
  color: #3b6fd4;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #c5d7f6;
}

.ai-quota-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  padding: 14px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  margin-bottom: 20px;
}
.ai-quota-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
.ai-quota-label {
  color: #666;
}
.ai-quota-value {
  font-size: 22px;
  color: #5b21b6;
}
.color-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.color-field-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.ai-topup-packages {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.ai-topup-package-btn {
  border: 1px solid #ddd6fe;
  background: #faf5ff;
  color: #4c1d95;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
}

.ai-topup-package-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pkg-name {
  font-size: 13px;
  font-weight: 600;
}

.pkg-meta {
  font-size: 12px;
  color: #6d28d9;
}

.ai-topup-orders {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: auto;
}

.ai-topup-orders table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
}

.ai-topup-orders th,
.ai-topup-orders td {
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  padding: 8px 10px;
  font-size: 12px;
}

.ai-topup-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}

.ai-topup-status.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.ai-topup-status.status-paid {
  background: #dcfce7;
  color: #166534;
}

.ai-topup-status.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

</style>
